import { CleaveOptions } from 'cleave.js/options'
import Cleave from 'cleave.js/react'
import { ChangeEvent } from 'cleave.js/react/props'
import { useField, useFormikContext } from 'formik'
import React, { CSSProperties } from 'react'

import FieldError from '@/components/form/FieldError'
import FieldLabel, { TooltipHint } from '@/components/form/FieldLabel'
import { FieldDescription, FieldGroup } from '@/components/ui/field'
import { InputGroup, InputGroupAddon } from '@/components/ui/input-group'
import { cn } from '@/lib/utils'

export interface MaskedInputProps {
  autoFocus?: boolean
  name: string
  label?: string | React.ReactNode
  className?: string
  inputClassName?: string
  inputStyle?: CSSProperties
  placeholder?: string
  tooltipHint?: TooltipHint
  inputGroupText?: string
  inputGroupStyle?: CSSProperties
  maskOptions?: CleaveOptions
  useFormattedValue?: boolean
  min?: number
  max?: number
  transformRawValue?: (rawValue: string) => number | string | undefined
  isDisabled?: boolean
  onChange?: (value: string) => void
  style?: React.CSSProperties
  helpText?: string
  value?: string
  readOnly?: boolean
}

const MaskedInput: React.FC<MaskedInputProps> = ({
  autoFocus,
  name,
  label,
  placeholder,
  className,
  inputClassName,
  tooltipHint,
  inputGroupText,
  inputGroupStyle,
  inputStyle,
  maskOptions,
  useFormattedValue = false,
  min,
  max,
  transformRawValue,
  isDisabled,
  onChange,
  style,
  helpText,
  value,
  readOnly,
}) => {
  const { setFieldValue, setFieldTouched, submitCount } = useFormikContext()
  const [field, { error }] = useField(name)
  const id = `field_${name}`

  const showError = !!(submitCount > 0 && error)

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    // Cleave creates the event.target.rawValue property.
    // (https://github.com/nosir/cleave.js/blob/master/src/Cleave.react.js#L291)
    // Pass this raw value to Formik.
    const rawValue = useFormattedValue
      ? event.target.value
      : event.target.rawValue

    setFieldTouched(name, true)
    setFieldValue(
      name,
      transformRawValue ? transformRawValue(rawValue) : rawValue,
    )

    if (onChange) {
      onChange(rawValue)
    }
  }

  function getFinalValue() {
    if (value) {
      return value
    }

    // Check if field.value is a number to account for 0 being falsy in JavaScript
    if (field.value || typeof field.value === 'number') {
      return field.value
    }

    return ''
  }

  const finalValue = getFinalValue()

  return (
    <FieldGroup data-disabled={isDisabled} className={className} style={style}>
      {label && (
        <FieldLabel htmlFor={id} tooltipHint={tooltipHint}>
          {label}
        </FieldLabel>
      )}

      <InputGroup style={inputGroupStyle}>
        {inputGroupText && (
          <InputGroupAddon align="inline-start">
            {inputGroupText}
          </InputGroupAddon>
        )}

        <Cleave
          autoFocus={autoFocus}
          id={id}
          {...field}
          value={finalValue}
          onChange={handleChange}
          data-slot="input-group-control"
          data-field={id}
          placeholder={placeholder}
          className={cn(
            'input',
            `flex-1 rounded-none border-0 shadow-none focus-visible:ring-0
            disabled:bg-(--disabled)`,
            inputClassName,
          )}
          style={inputStyle}
          options={maskOptions || {}}
          min={min}
          max={max}
          disabled={isDisabled}
          readOnly={readOnly}
        />
      </InputGroup>

      {helpText && <FieldDescription>{helpText}</FieldDescription>}

      {showError && <FieldError inputId={id}>{error}</FieldError>}
    </FieldGroup>
  )
}

export default MaskedInput
